import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import { InfoTooltip } from '@/components/Tooltip';
import EmptyState from '@/components/EmptyState';
import ContextualHelp from '@/components/ContextualHelp';
import {
  Sparkles, ArrowRight, Flame, Lock, TrendingUp, BookOpen, Trophy, Award,
  ClipboardList, ChevronDown, ChevronUp, Info,
} from 'lucide-react';

const PROGRESS_HELP = {
  whatIs: "Your growth over time — check-ins, wellness trends, lessons completed, milestones, and the Weekly Wellness Review.",
  whyMatters: "Change is rarely linear. Seeing your data in one place shows the trend even when a single day feels flat. It's also where the AI Wellness Review lives.",
  howToUse: "Open weekly. Read your Weekly Review the moment it unlocks (every 7 check-ins). Milestones move slowly on purpose — consistency compounds.",
  tips: [
    "The Wellness Score is a compass, not a report card. Watch the trend, not any single day.",
    "If a streak breaks, start again tomorrow. Nothing else matters.",
    "The Weekly Review is where the biggest insights live — read it out loud if you can.",
  ],
};

// Emoji glyph shown at the top-left of each metric card
const METRIC_EMOJI = { wellness: '🧭', mood: '🙂', energy: '⚡', sleep: '😴' };

/** MetricCard — answers 3 questions for each score:
 *  What does this number mean? Is it good/average/needs attention? What should I do next?
 *  Layout: score + trend delta → band chip → segmented band bar → recommendation w/ CTA. */
function MetricCard({ metricKey, m, sparkline }) {
  const [showExplain, setShowExplain] = useState(false);
  const cur = m.current;
  const prev = m.previous;
  const delta = m.delta;
  const band = m.band;
  const ranges = m.typical_ranges || [];
  const min = m.scale_min ?? 0;
  const max = m.scale_max ?? 100;

  const hasCurrent = typeof cur === 'number';
  const positionPct = hasCurrent ? Math.min(100, Math.max(0, ((cur - min) / (max - min)) * 100)) : 0;

  // Direction of change (higher is better for all four metrics)
  const trendDir = typeof delta === 'number' ? (delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat')) : null;

  return (
    <div
      className="bg-white rounded-3xl p-5 lg:p-6 card-soft flex flex-col"
      data-testid={`metric-${metricKey}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold flex items-center gap-1.5">
            <span aria-hidden="true">{METRIC_EMOJI[metricKey]}</span> {m.title}
          </p>
          <div className="flex items-baseline gap-2 mt-2" data-testid={`metric-${metricKey}-value`}>
            <p className="font-heading text-3xl lg:text-4xl font-light text-navy tabular-nums leading-none">
              {hasCurrent ? cur : '—'}
            </p>
            <p className="text-sm text-navy/40 font-medium">{m.unit_label}</p>
          </div>
        </div>
        <button
          onClick={() => setShowExplain(v => !v)}
          data-testid={`metric-${metricKey}-info`}
          className="w-8 h-8 rounded-full bg-beige hover:bg-[#EAE5D9] flex items-center justify-center text-navy/60 hover:text-navy transition-smooth shrink-0"
          aria-label="What is this score?"
        >
          <Info size={14}/>
        </button>
      </div>

      {/* Trend delta line */}
      {trendDir && (
        <p className="text-xs text-navy/60 mt-1 tabular-nums" data-testid={`metric-${metricKey}-trend`}>
          {trendDir === 'up' && <span className="text-green font-semibold">▲ +{delta}</span>}
          {trendDir === 'down' && <span className="text-red-600 font-semibold">▼ {delta}</span>}
          {trendDir === 'flat' && <span className="text-navy/50 font-semibold">— 0</span>}
          <span className="ml-1"> from last week</span>
        </p>
      )}
      {!trendDir && (
        <p className="text-xs text-navy/45 mt-1">Keep checking in — trends unlock after one week.</p>
      )}

      {/* Band chip */}
      {band && (
        <div
          className="inline-flex items-center gap-2 mt-4 self-start px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-semibold"
          style={{ backgroundColor: `${band.color}15`, color: band.color }}
          data-testid={`metric-${metricKey}-band`}
        >
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: band.color }}/>
          {band.label}
        </div>
      )}

      {/* Segmented band bar with current-position indicator */}
      {ranges.length > 0 && (
        <div className="mt-4">
          <div className="relative h-2 rounded-full overflow-hidden flex" data-testid={`metric-${metricKey}-bar`}>
            {ranges.map((r, i) => {
              const rMin = Math.max(min, r.min);
              const rMax = Math.min(max, r.max);
              const w = ((rMax - rMin) / (max - min)) * 100;
              return (
                <div
                  key={r.label || `range-${i}`}
                  className="h-full"
                  style={{ width: `${w}%`, backgroundColor: `${r.color}70` }}
                  title={r.label}
                />
              );
            })}
            {hasCurrent && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 shadow-md transition-all"
                style={{ left: `calc(${positionPct}% - 8px)`, borderColor: band?.color || '#0B192C' }}
                aria-label={`Current position: ${cur}`}
              />
            )}
          </div>
          <div className="flex justify-between mt-1.5 text-[9px] uppercase tracking-widest text-navy/40 tabular-nums">
            <span>{min}</span>
            <span>{max}</span>
          </div>
        </div>
      )}

      {/* Explainer — opens on info-icon click */}
      {showExplain && (
        <div className="mt-4 bg-beige rounded-2xl p-4 text-xs lg:text-sm text-navy/75 leading-relaxed space-y-2" data-testid={`metric-${metricKey}-explain`}>
          <p className="font-semibold text-navy">What is this score?</p>
          <p>{m.explainer}</p>
          {ranges.length > 0 && (
            <div className="pt-1 space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">Typical range</p>
              {ranges.map((r, i) => (
                <div key={r.label || `range-${i}`} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }}/>
                  <span className="tabular-nums font-medium text-navy/80">{r.min}–{r.max}</span>
                  <span className="text-navy/60">= {r.label}</span>
                </div>
              ))}
            </div>
          )}
          {typeof prev === 'number' && (
            <p className="pt-1 text-[11px] text-navy/60">Last week average: <span className="font-semibold text-navy/80 tabular-nums">{prev}</span></p>
          )}
        </div>
      )}

      {/* Recommendation with CTA */}
      {m.recommendation && (
        <div className="mt-5 pt-4 border-t border-navy/10 flex-1 flex flex-col justify-end">
          <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-1.5">
            Your next best step
          </p>
          <p className="text-sm text-navy/85 leading-relaxed" data-testid={`metric-${metricKey}-rec`}>
            {m.recommendation.text}
          </p>
          <Link
            to={m.recommendation.cta_to}
            data-testid={`metric-${metricKey}-cta`}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-navy hover:text-gold transition-smooth self-start"
          >
            {m.recommendation.cta_label} <ArrowRight size={14}/>
          </Link>
        </div>
      )}
      {sparkline}
    </div>
  );
}

/** WellnessCompositionCard — expandable "How your Wellness Score is calculated" section */
function WellnessCompositionCard({ composition }) {
  const [open, setOpen] = useState(false);
  if (!composition) return null;
  return (
    <div className="bg-white rounded-3xl card-soft overflow-hidden" data-testid="wellness-composition">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="wellness-composition-toggle"
        className="w-full flex items-center justify-between p-5 lg:p-6 hover:bg-beige/40 transition-smooth"
      >
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">Transparency</p>
          <p className="font-heading text-base lg:text-lg text-navy mt-1">How your Wellness Score is calculated</p>
        </div>
        {open ? <ChevronUp size={18} className="text-navy/60"/> : <ChevronDown size={18} className="text-navy/60"/>}
      </button>
      {open && (
        <div className="px-5 lg:px-6 pb-5 lg:pb-6">
          <p className="text-sm text-navy/70 leading-relaxed mb-4">{composition.intro}</p>
          <div className="space-y-2">
            {composition.contributors.map((c, i) => (
              <div key={c.label || `contrib-${i}`} className="flex items-center gap-3" data-testid={`composition-${i}`}>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-navy/85">{c.label}</span>
                    <span className="text-xs font-semibold text-navy tabular-nums">{c.weight_pct}%</span>
                  </div>
                  <div className="h-1.5 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-navy" style={{ width: `${c.weight_pct}%` }}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-navy/45 mt-4 italic leading-relaxed">{composition.note}</p>
        </div>
      )}
    </div>
  );
}

const JourneyStat = ({ label, value, tooltipText, testid }) => (
  <div className="bg-white rounded-2xl p-4 lg:p-5 card-soft" data-testid={testid || `journey-${label}`}>
    <p className="text-[10px] lg:text-xs uppercase tracking-widest text-navy/50 font-semibold inline-flex items-center gap-1">
      {label}
      {tooltipText && <InfoTooltip content={tooltipText} testid={`tt-journey-${label}`}/>}
    </p>
    <p className="font-heading text-2xl lg:text-4xl mt-1 font-light tabular-nums">{value ?? '—'}</p>
  </div>
);

/** Growth Plan Journey card — active plan progress + CTA, or "Start one" empty state. */
function GrowthPlanCard({ active }) {
  if (active) {
    return (
      <Link
        to={active.cta_to || `/app/plans/${active.plan_id}`}
        data-testid="journey-growth-plan"
        className="bg-navy text-white rounded-2xl p-4 lg:p-5 card-soft hover:-translate-y-0.5 transition-smooth block col-span-2 sm:col-span-1 lg:col-span-1"
      >
        <p className="text-[10px] lg:text-xs uppercase tracking-widest text-gold font-semibold inline-flex items-center gap-1">
          Growth Plan
        </p>
        <p className="font-heading text-sm lg:text-base mt-1 leading-tight truncate" title={active.title}>{active.title}</p>
        <p className="text-[11px] text-white/60 mt-0.5">Day {active.current_day} of {active.days}</p>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-3">
          <div className="h-full bg-gold transition-all" style={{ width: `${active.progress_pct}%` }}/>
        </div>
        <p className="text-[11px] text-gold font-semibold mt-1.5 tabular-nums">{active.progress_pct}% complete</p>
      </Link>
    );
  }
  return (
    <Link
      to="/app/plans"
      data-testid="journey-growth-plan-empty"
      className="bg-white rounded-2xl p-4 lg:p-5 card-soft hover:-translate-y-0.5 transition-smooth block col-span-2 sm:col-span-1 lg:col-span-1 border border-dashed border-navy/15"
    >
      <p className="text-[10px] lg:text-xs uppercase tracking-widest text-navy/50 font-semibold">Growth Plan</p>
      <p className="font-heading text-sm lg:text-base text-navy mt-1 leading-tight">No active plan</p>
      <p className="text-[11px] text-navy/50 mt-0.5">Small daily steps → real change.</p>
      <p className="text-[11px] text-gold font-semibold mt-3 inline-flex items-center gap-1">
        Start one →
      </p>
    </Link>
  );
}

const tierClass = (tier) => {
  if (tier === 'gold') return 'from-gold/30 to-gold/10 ring-gold/50 text-gold';
  if (tier === 'silver') return 'from-navy/20 to-navy/5 ring-navy/40 text-navy';
  return 'from-beige to-white ring-navy/10 text-navy/70';
};

// What Weekly Review actually analyzes — shown in the locked state to build anticipation
const WEEKLY_REVIEW_AREAS = [
  { emoji: '🙂', label: 'Mood patterns' },
  { emoji: '🌪️', label: 'Stress signals' },
  { emoji: '😴', label: 'Sleep quality' },
  { emoji: '⚡', label: 'Energy trends' },
  { emoji: '📝', label: 'Journal themes' },
  { emoji: '📖', label: 'Lesson completion' },
  { emoji: '💬', label: 'Coach conversations' },
];

export default function Progress() {
  const [data, setData] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [journey, setJourney] = useState(null);

  useEffect(() => {
    http.get('/progress/summary').then(r => setData(r.data));
    http.get('/checkins/patterns').then(r => setPatterns(r.data)).catch(()=>{});
    http.get('/progress/weekly-review').then(r => setWeekly(r.data)).catch(()=>{});
    http.get('/progress/journey').then(r => setJourney(r.data)).catch(()=>{});
  }, []);

  if (!data) return <div className="p-6 text-navy/60">Loading your journey…</div>;

  const metrics = data.metrics || {};
  const daysUntilTrends = Math.max(0, 7 - (data.checkins_last_7d || 0));

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 lg:space-y-10" data-testid="progress-page">
      {/* HERO */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Progress</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2 leading-tight flex items-start gap-2 flex-wrap">
          Your journey.
          <ContextualHelp pageTitle="Progress" content={PROGRESS_HELP} testid="progress-help"/>
        </h1>
        <p className="hidden lg:block text-navy/60 text-base lg:text-lg mt-2 lg:mt-3 max-w-2xl">
          Every check-in, journal, and lesson adds up. Here&apos;s the shape of the last few weeks — and what to do next.
        </p>
      </div>

      {/* YOUR JOURNEY STATS */}
      <div>
        <p className="font-heading text-lg lg:text-2xl mb-3 lg:mb-4 flex items-center gap-2">
          Your Journey
          <InfoTooltip content="Your total activity in Brotherhood+. Every entry counts." testid="tt-journey"/>
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <JourneyStat label="Days active" value={journey?.stats?.days_active} tooltipText="Unique days you've checked in, journaled, or completed a lesson."/>
          <JourneyStat label="Streak" value={journey?.stats?.current_streak} tooltipText="Consecutive days of check-ins. Resets after a missed day."/>
          <JourneyStat label="Check-ins" value={journey?.stats?.checkins} tooltipText="Total check-ins you've submitted."/>
          <JourneyStat label="Lessons" value={journey?.stats?.lessons_completed} tooltipText="Lessons completed all the way through."/>
          <JourneyStat label="Journals" value={journey?.stats?.journals} tooltipText="Journal entries you've written. Private to you."/>
          <JourneyStat label="Wellness" value={journey?.stats?.wellness_score ?? '—'} tooltipText="Latest wellness score from your most recent assessment."/>
          <GrowthPlanCard active={journey?.active_growth_plan}/>
        </div>
      </div>

      {/* THIS WEEK — redesigned metric cards */}
      <div>
        <div className="flex items-baseline justify-between mb-3 lg:mb-4">
          <p className="font-heading text-lg lg:text-2xl flex items-center gap-2">
            This Week
            <InfoTooltip content="Each card shows what the number means, whether it's tracking well, and your next best action." testid="tt-thisweek"/>
          </p>
          {daysUntilTrends > 0 && (
            <p className="text-xs text-navy/50" data-testid="trends-unlock-hint">
              Personalized trends after {daysUntilTrends} more check-in{daysUntilTrends === 1 ? '' : 's'}.
            </p>
          )}
        </div>
        {data.checkins_count === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="Your mood trends will appear after your first daily check-in."
            description="One under-60-second check-in a day is enough. Keep going — trends unlock after one week."
            ctaLabel="Do today's check-in"
            ctaTo="/app/checkin"
            testid="empty-thisweek"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {['wellness', 'mood', 'energy', 'sleep'].map(k => metrics[k] ? (
              <MetricCard key={k} metricKey={k} m={metrics[k]}/>
            ) : null)}
          </div>
        )}
      </div>

      {/* HOW YOUR WELLNESS SCORE IS CALCULATED */}
      {data.wellness_composition && (
        <WellnessCompositionCard composition={data.wellness_composition}/>
      )}

      {/* WEEKLY AI WELLNESS REVIEW */}
      <div>
        <p className="font-heading text-lg lg:text-2xl mb-3 lg:mb-4 flex items-center gap-2">
          Weekly AI Wellness Review
          <InfoTooltip content="Every 7 check-ins Brotherhood+ analyzes your patterns and gives personalized guidance." testid="tt-weekly"/>
        </p>
        {weekly && !weekly.unlocked && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="weekly-review-locked">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-2xl bg-beige flex items-center justify-center shrink-0">
                <Lock size={18} className="text-navy/60"/>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Unlocks after 7 daily check-ins</p>
                <p className="font-heading text-lg lg:text-xl text-navy mt-1">Here&apos;s what your AI mentor will surface.</p>
                <p className="text-sm lg:text-base text-navy/70 mt-2 leading-relaxed">
                  After 7 daily check-ins, Brotherhood+ identifies patterns across every area you&apos;ve been working on:
                </p>
                <ul className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4">
                  {WEEKLY_REVIEW_AREAS.map((a, i) => (
                    <li key={a.label || `area-${i}`} className="flex items-center gap-2 text-sm text-navy/80" data-testid={`weekly-area-${i}`}>
                      <span aria-hidden="true">{a.emoji}</span> {a.label}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-navy/70 mt-4 leading-relaxed">
                  Then your AI mentor surfaces personalized insights, strengths, and next steps.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-gold transition-all duration-700" style={{ width: `${Math.min(100, (weekly.days_logged / 7) * 100)}%` }}/>
                  </div>
                  <span className="text-xs font-medium text-navy/70 tabular-nums">{weekly.days_logged}/7</span>
                </div>
                <Link to="/app/checkin" data-testid="weekly-checkin-cta" className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-navy hover:text-gold">
                  Do today&apos;s check-in <ArrowRight size={14}/>
                </Link>
              </div>
            </div>
          </div>
        )}
        {weekly?.unlocked && (
          <div className="bg-white rounded-3xl p-6 lg:p-10 card-soft" data-testid="weekly-review-card">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles size={20} className="text-gold"/>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Weekly Wellness Review</p>
              <span className="ml-auto text-xs text-navy/40">{weekly.range?.from} → {weekly.range?.to}</span>
            </div>
            <p className="font-heading text-xl lg:text-3xl text-navy leading-snug">Here&apos;s what we noticed this week.</p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mt-6">
              {['mood', 'stress', 'sleep', 'energy'].map(k => (
                <div key={k} className="bg-beige/60 rounded-2xl p-4 lg:p-5">
                  <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">{k}</p>
                  <p className="text-sm lg:text-base text-navy/80 mt-1 leading-snug">{weekly.patterns?.[k]}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-6">
              {weekly.strengths?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-green font-semibold mb-2">Strengths</p>
                  <ul className="space-y-1.5">
                    {weekly.strengths.map(s => (
                      <li key={s} className="text-sm text-navy/75 leading-relaxed flex gap-2"><span className="text-green mt-1">•</span>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {weekly.growth_areas?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-2">Your biggest opportunities</p>
                  <ul className="space-y-1.5">
                    {weekly.growth_areas.map(g => (
                      <li key={g} className="text-sm text-navy/75 leading-relaxed flex gap-2"><span className="text-gold mt-1">•</span>{g}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {weekly.clinical_recommendations?.length > 0 && (
              <div className="mt-6 bg-navy text-white rounded-2xl p-5 lg:p-6">
                <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-3">Clinical recommendations</p>
                <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
                  {weekly.clinical_recommendations.map(r => (
                    <li key={r} className="text-sm text-white/85 leading-relaxed flex gap-2"><span className="text-gold mt-1">•</span>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              {weekly.suggested_lessons?.[0] && (
                <Link to={`/app/learn/${weekly.suggested_lessons[0].id}`} data-testid="weekly-suggested-lesson"
                  className="group bg-beige rounded-2xl p-5 hover:bg-[#EAE5D9] transition-smooth">
                  <div className="flex items-start gap-3">
                    <BookOpen size={18} className="text-navy shrink-0 mt-0.5"/>
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">Recommended lesson</p>
                      <p className="font-heading text-base text-navy mt-1">{weekly.suggested_lessons[0].title}</p>
                      <p className="text-xs text-navy/60 mt-1">{weekly.suggested_lessons[0].why}</p>
                    </div>
                  </div>
                </Link>
              )}
              {weekly.suggested_plan && (
                <Link to={`/app/plans/${weekly.suggested_plan.id}`} data-testid="weekly-suggested-plan"
                  className="group bg-beige rounded-2xl p-5 hover:bg-[#EAE5D9] transition-smooth">
                  <div className="flex items-start gap-3">
                    <TrendingUp size={18} className="text-navy shrink-0 mt-0.5"/>
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">Recommended plan</p>
                      <p className="font-heading text-base text-navy mt-1">{weekly.suggested_plan.title}</p>
                      <p className="text-xs text-navy/60 mt-1">{weekly.suggested_plan.why}</p>
                    </div>
                  </div>
                </Link>
              )}
            </div>

            <p className="text-[11px] text-navy/40 mt-6 leading-relaxed">{weekly.disclaimer}</p>
          </div>
        )}
      </div>

      {/* AI INSIGHTS */}
      <div>
        <p className="font-heading text-lg lg:text-2xl mb-3 lg:mb-4 flex items-center gap-2">
          AI Insights
          <InfoTooltip content="Warm, non-judgmental observations from your recent patterns. Not a diagnosis." testid="tt-insights"/>
        </p>
        {journey?.narrative_insights?.length > 0 ? (
          <div className="bg-navy text-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="ai-narrative-card">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles size={18} className="text-gold"/>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">What we&apos;ve noticed</p>
              {patterns?.streak_days > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 text-xs bg-gold/20 border border-gold/30 rounded-full px-3 py-1">
                  <Flame size={12} className="text-gold"/> {patterns.streak_days}-day streak
                </span>
              )}
            </div>
            <ul className="space-y-2.5 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
              {journey.narrative_insights.map((s, i) => (
                <li key={`insight-${i}-${(s || '').slice(0, 32)}`} className="text-sm lg:text-base text-white/85 leading-relaxed flex items-start gap-2">
                  <span className="text-gold mt-1">•</span><span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-white/40 mt-4">Supportive observations only. Not a diagnosis.</p>
          </div>
        ) : (
          <EmptyState
            icon={Sparkles}
            title="Insights appear as you build a rhythm."
            description="After a few check-ins we start noticing patterns and share them here."
            ctaLabel="Do today's check-in"
            ctaTo="/app/checkin"
            testid="empty-insights"
          />
        )}
      </div>

      {/* MILESTONES */}
      <div>
        <p className="font-heading text-lg lg:text-2xl mb-3 lg:mb-4 flex items-center gap-2">
          Milestones
          <InfoTooltip content={`Earned ${journey?.milestones?.earned_count || 0} of ${journey?.milestones?.total || 0}. Keep going.`} testid="tt-milestones"/>
        </p>
        {journey?.milestones?.items?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="milestones-grid">
            {journey.milestones.items.map(m => (
              <div
                key={m.id}
                data-testid={`milestone-${m.id}`}
                className={`relative rounded-3xl p-5 lg:p-6 flex flex-col gap-2 items-start transition-smooth bg-gradient-to-br ${
                  m.earned
                    ? `${tierClass(m.tier)} ring-1 card-soft`
                    : 'from-navy/[0.04] to-navy/[0.02] ring-1 ring-navy/10 opacity-70 saturate-0'
                }`}
              >
                <div
                  className={`text-5xl lg:text-6xl leading-none ${m.earned ? '' : 'grayscale opacity-40'}`}
                  aria-hidden="true"
                >
                  {m.emoji}
                </div>
                <p className={`font-heading text-sm lg:text-base leading-tight mt-1 ${m.earned ? '' : 'text-navy/50'}`}>
                  {m.title}
                </p>
                <p className={`text-[11px] leading-snug ${m.earned ? 'text-navy/60' : 'text-navy/40'}`}>
                  {m.description}
                </p>
                {!m.earned && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-navy/10 text-navy/60 ring-1 ring-navy/15"
                    data-testid={`milestone-lock-${m.id}`}
                    aria-label="Locked milestone"
                  >
                    <Lock size={20} strokeWidth={1.75}/>
                  </span>
                )}
                {m.earned && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-gold/20 ring-1 ring-gold/40 text-gold"
                    data-testid={`milestone-trophy-${m.id}`}
                    aria-label="Milestone earned"
                  >
                    <Trophy size={20} strokeWidth={1.75}/>
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Award}
            title="Milestones appear as you build momentum."
            description="First check-in, first lesson, first journal — small wins that compound."
            testid="empty-milestones"
          />
        )}
      </div>
    </div>
  );
}
